/**
 * Marketing landing solution demo — topics, EXP, engagement/comments seeds (`landing-demo-f*` ids).
 */
import type {
	LandingDemoTopicEngagementPersisted,
	LandingDemoTopicStopProgress,
} from "@marketing/home/lib/landing-demo-storage";
import { applyTopicCardCompletedRepeatMultiplier } from "@repo/api/modules/rewards/domain/repeat-multiplier";
import type { TopicContentTopic } from "@saas/shared/lib/topic-content/topic-content-types";
import { getTopicCardRequiredDurationMs } from "@saas/learn/lib/get-topic-card-required-duration-ms";
import type { TopicContinuationTimerPolicy } from "@saas/learn/lib/topic-continuation-timer-policy";

export const LANDING_DEMO_FEATURE_BIOMES = [
	"DESERT",
	"FOREST",
	"VOLCANO",
] as const;

/** Grid keys: 3 pillars × 3 topics — matches {@link FEATURES} in SolutionDemo. */
export const LANDING_DEMO_INTERACTABLE_KEYS = [
	["landing-demo-f0-t0", "landing-demo-f0-t1", "landing-demo-f0-t2"],
	["landing-demo-f1-t0", "landing-demo-f1-t1", "landing-demo-f1-t2"],
	["landing-demo-f2-t0", "landing-demo-f2-t1", "landing-demo-f2-t2"],
] as const;

export interface LandingDemoTopicCommentSeed {
	id: string;
	userDisplayName: string;
	body: string;
	createdAtIso: string;
}

export interface LandingDemoTopicSocialSeed {
	counts: {
		likeCount: number;
		saveCount: number;
		commentCount: number;
		shareCount: number;
	};
	viewer: {
		hasLiked: boolean;
		hasSaved: boolean;
		hasCommented: boolean;
		hasShared: boolean;
	};
	comments: LandingDemoTopicCommentSeed[];
}

const TOPIC_LABELS: [string, string, string][] = [
	["Training made easy", "Launch to your team", "Optimize the outcomes"],
	[
		"Bring learning to life",
		"From knowledge to habits",
		"Your personal learning coach",
	],
	["Answers on demand", "Actions on the fly", "We're just getting started"],
];

/** Root for per-card demo art — one file per slot (`f{pillar}-t{topic}-c{card}.png`). */
export const LANDING_DEMO_CARD_IMAGE_ROOT = "/images/landing-page/demo-cards";

export interface LandingDemoCardSubtextLink {
	phrase: string;
	kind: "mailto" | "path";
	href?: string;
}

/** Authoring copy for one demo card; set `imageSrc` to override the slot placeholder. */
export interface LandingDemoCardCopySeed {
	title: string;
	subtext: string;
	imageSrc?: string;
	imageAlt?: string;
	subtextLink?: LandingDemoCardSubtextLink;
}

export interface LandingDemoCardPresentationSeed {
	title: string;
	imageSrc: string;
	imageAlt: string;
	subtext: string;
	subtextLink?: LandingDemoCardSubtextLink;
}

type LandingDemoTopicCardPresentationGrid =
	LandingDemoCardPresentationSeed[][][];

/** Placeholder image path for pillar × topic × card (0-based indices). */
export function landingDemoCardImagePath(
	featureIndex: number,
	topicIndex: number,
	cardIndex: number,
): string {
	return `${LANDING_DEMO_CARD_IMAGE_ROOT}/f${featureIndex}-t${topicIndex}-c${cardIndex}.png`;
}

function landingDemoTopicCards(
	featureIndex: number,
	topicIndex: number,
	cards: ReadonlyArray<LandingDemoCardCopySeed>,
): LandingDemoCardPresentationSeed[] {
	return cards.map((card, cardIndex) => ({
		title: card.title,
		subtext: card.subtext,
		imageSrc:
			card.imageSrc?.trim() ||
			landingDemoCardImagePath(featureIndex, topicIndex, cardIndex),
		imageAlt: card.imageAlt ?? `${card.title} icon`,
		subtextLink: card.subtextLink,
	}));
}

/**
 * Per-topic card copy for the landing solution demo.
 * Grid order matches {@link TOPIC_LABELS} (pillar row × topic column).
 * Each card gets `landingDemoCardImagePath(pillar, topic, card)` unless `imageSrc` is set.
 */
export const LANDING_DEMO_TOPIC_CARD_PRESENTATION: LandingDemoTopicCardPresentationGrid =
	[
		[
			landingDemoTopicCards(0, 0, [
				{
					title: "Start with a conversation",
					subtext:
						"Creating training micro-lessons like this is as simple as speaking into your phone. Just chat with our AI to guide you through.",
				},
				{
					title: "Upload your existing content",
					subtext:
						"Got some wordy docs, old decks, or scribbled notes? Just drop them in – they'll be transcribed and used as source materials.",
				},
				{
					title: "Or start from scratch",
					subtext:
						"Don't have any content? No problem. Just describe what you need, who it's for, and any goals, then we'll generate it for you.",
				},
				{
					title: "Publish in minutes, not months",
					subtext:
						"Within minutes, you'll have a draft ready. Make changes conversationally, preview as a learner, and publish to your team.",
				},
				{
					title: "Scale your team's skills with ease",
					subtext:
						"Integrate our library of pre-built content – ever-expanding with the latest high-value skills from critical thinking to leadership.",
				},
			]),
			landingDemoTopicCards(0, 1, [
				{
					title: "Sharing is simple, whenever you want",
					subtext:
						"Once published, you can download, distribute, or schedule content to your team – no need to manage separate systems.",
				},
				{
					title: "Collaborate across teams in real-time",
					subtext:
						"Whether peer-reviews, approval flows, or quality checks, SkillQuest makes it easy for teams to get unstuck and make progress.",
				},
				{
					title: "Fresh content, minus the headaches",
					subtext:
						"Backed by full version control, you can easily track changes, revert mistakes, and keep your content fresh – all in one place.",
				},
			]),
			landingDemoTopicCards(0, 2, [
				{
					title: "Why deliver training online, anyway?",
					subtext:
						"One of the best things about digitizing your training content is the ability to track progress and measure impact.",
				},
				{
					title: "Analytics embedded throughout",
					subtext:
						"Every piece of content comes with built-in analytics – from engagement rates, to completion times, and sticking points.",
				},
				{
					title: "Insights surfaced for you",
					subtext:
						"No time to analyse the data? We got you covered – our agents will surface the insights that matter most automatically.",
				},
				{
					title: "Data-driven experimentation",
					subtext:
						"Not sure how to act on the insights? Our agents will help you make the right changes – experimentation with confidence.",
				},
			]),
		],
		[
			landingDemoTopicCards(1, 0, [
				{
					title: "We've all been there...",
					subtext:
						"Mindlessly clicking through corporate training courses, another slide deck, more docs – forgetting it all by next week.",
				},
				{
					title: "It's time to change that",
					subtext:
						"Imagine learning being as fun and rewarding as playing a game – where you can learn anything, anytime, anywhere.",
				},
				{
					title: "Enter game-based learning",
					subtext:
						"Compliance training to do? Now it's a quest to fight an ogre. Master new skills to level up your character – and your career.",
				},
				{
					title: "Gaming? You lost me there",
					subtext:
						"3 in 5 adults are gamers. But SkillQuest isn't just for gamers – we adapt the experience, while keeping proven mechanics.",
				},
				{
					title: "Is this just gamification?",
					subtext:
						"Gamified learning isn't new. We go beyond points, badges and leaderboards to craft a truly engaging game-based experience.",
				},
			]),
			landingDemoTopicCards(1, 1, [
				{
					title: "Knowledge =/= application",
					subtext:
						"LLMs democratize access to knowledge. But they don't make it easier to apply. Most training lacks practical follow-up.",
				},
				{
					title: "STEP 1: Learn --> Understand",
					subtext:
						"Once training is done, most content sits idle. SkillQuest aids understanding through tailored practice and personal coaching.",
				},
				{
					title: "STEP 2: Understand --> Apply",
					subtext:
						"Understanding concepts is one thing. Applying them in real-world scenarios is harder. AI-driven role-play bridges the gap.",
				},
				{
					title: "STEP 3: Apply --> Habits",
					subtext:
						"Building habits requires consistency. SkillQuest keeps you accountable with regular practice and real-world challenges.",
				},
			]),
			landingDemoTopicCards(1, 2, [
				{
					title: "A friendly coach, always by your side",
					subtext:
						"Our friendly agents are characters in the game world – there to support and guide you through your learning journey.",
				},
				{
					title: "Dive deeper anytime you need",
					subtext:
						"Want to dive deeper on a topic? Our agents can provide explanations, examples, and practice quizzes – whenever you need them.",
				},
				{
					title: "Personalized to your preferences",
					subtext:
						"Content adapts dynamically to your learning style, pace, and preferences – listen, read, or watch as you prefer.",
				},
				{
					title: "Set goals and stay accountable",
					subtext:
						"Set your professional development goals and track progress – our agents will keep you on track and motivated, every step.",
				},
			]),
		],
		[
			landingDemoTopicCards(2, 0, [
				{
					title: "A team of agents, ready to help 24/7",
					subtext:
						"Whether you're a learner, creator, or leader – our AI learns your organization's context and goals to help you succeed.",
				},
				{
					title: "Ask anything, from anywhere",
					subtext:
						"You can access conversations from anywhere in the platform, and our agents will take you where you need to go.",
				},
				{
					title: "Can't find what you need?",
					subtext:
						"A conversation can help you find it. We organize all platform data and enterprise knowledge to be easily searchable.",
				},
			]),
			landingDemoTopicCards(2, 1, [
				{
					title: "Anything manual can be automated",
					subtext:
						"Still spending time clicking around? Our agents are wired up with in-platform tools to help get your workflow done faster.",
				},
				{
					title: "Spin up analytics in minutes",
					subtext:
						"Less time wasted waiting around for reports to run – your latest L&D data turned into dashboards built on the fly, using AI.",
				},
				{
					title: "Measure what matters most",
					subtext:
						"Leaders get insights into behavioural data that goes beyond course completions into real-world application and habit formation.",
				},
			]),
			landingDemoTopicCards(2, 2, [
				{
					title: "Help us build our roadmap, together",
					subtext:
						"Our team is constantly learning and iterating from customer feedback – waitlist members can submit feature requests straight to us.",
				},
				{
					title: "Need help with your decision?",
					subtext:
						"Not sure if SkillQuest is right for you? Feel free to drop us an email and we'll be happy to schedule a call to help you explore more.",
					subtextLink: { phrase: "drop us an email", kind: "mailto" },
				},
				{
					title: "That's all, back to building!",
					subtext:
						"If you made it this far, you're probably ready to try it out for yourself. Join our waitlist for early access and let us know what you think!",
					subtextLink: {
						phrase: "Join our waitlist",
						kind: "path",
						href: "/waitlist?earlyaccess=true",
					},
				},
			]),
		],
	];

/** Static topic-viewer art used alongside demo card icons (cover swipe hint). */
const LANDING_DEMO_TOPIC_VIEWER_STATIC_IMAGE_SRCS = [
	"/images/swipe.png",
] as const;

/** Unique demo card icon paths from {@link LANDING_DEMO_TOPIC_CARD_PRESENTATION}. */
export function getLandingDemoTopicCardImageSrcs(): readonly string[] {
	const imageSrcs = new Set<string>(
		LANDING_DEMO_TOPIC_VIEWER_STATIC_IMAGE_SRCS,
	);
	for (const topicRow of LANDING_DEMO_TOPIC_CARD_PRESENTATION) {
		for (const topicCards of topicRow) {
			for (const card of topicCards) {
				const trimmed = card.imageSrc.trim();
				if (trimmed) {
					imageSrcs.add(trimmed);
				}
			}
		}
	}
	return [...imageSrcs];
}

function demoTopicKey(featureIndex: number, topicIndex: number): string {
	return `landing-demo-f${featureIndex}-t${topicIndex}`;
}

function textBlock(id: string, text: string, orderIndex: number) {
	return {
		id,
		type: "TEXT",
		content: { content: text },
		orderIndex,
	};
}

function buildTopicCards(
	topicKey: string,
	presentationSeeds: ReadonlyArray<LandingDemoCardPresentationSeed>,
) {
	return presentationSeeds.map((presentation, orderIndex) => ({
		id: `${topicKey}-card-${orderIndex}`,
		name: presentation.title,
		orderIndex,
		blocks: [
			textBlock(
				`${topicKey}-block-${orderIndex}`,
				presentation.subtext,
				0,
			),
		],
		layoutTemplate: "demo",
		layoutProps: {
			title: presentation.title,
			imageSrc: presentation.imageSrc,
			imageAlt: presentation.imageAlt,
			subtext: presentation.subtext,
			subtextLink: presentation.subtextLink,
		},
	}));
}

function buildTopicsByKey(): Record<string, TopicContentTopic> {
	const out: Record<string, TopicContentTopic> = {};
	for (let f = 0; f < 3; f++) {
		for (let t = 0; t < 3; t++) {
			const key = demoTopicKey(f, t);
			const label = TOPIC_LABELS[f][t];
			const presentationSeeds =
				LANDING_DEMO_TOPIC_CARD_PRESENTATION[f][t];
			out[key] = {
				id: key,
				name: label,
				description: `Marketing demo · ${label}.`,
				skill: { name: label.split(" ")[0] ?? "Skill" },
				cards: buildTopicCards(key, presentationSeeds),
				quizQuestions: [],
			};
		}
	}
	return out;
}

export const LANDING_DEMO_TOPICS_BY_KEY: Record<string, TopicContentTopic> =
	buildTopicsByKey();

/** EXP per second of required dwell (dwell follows {@link getLandingDemoCardRequiredDurationMs}). */
const DEMO_CARD_EXP_PER_DWELL_SECOND_BY_ORDER = [0.6, 0.6, 0.5] as const;
const DEMO_CARD_EXP_PER_DWELL_SECOND_DEFAULT = 0.6;

/** Coins per second of required dwell (local wallet only; not tied to EXP boosts). */
const DEMO_CARD_COINS_PER_DWELL_SECOND = 5;

function getDemoCardOrderIndex(cardId: string): number {
	const match = /-card-(\d+)$/.exec(cardId);
	if (match == null) {
		return 0;
	}
	const orderIndex = Number.parseInt(match[1] ?? "0", 10);
	return Number.isFinite(orderIndex) ? orderIndex : 0;
}

function getDemoCardExpPerDwellSecond(cardId: string): number {
	const orderIndex = getDemoCardOrderIndex(cardId);
	return (
		DEMO_CARD_EXP_PER_DWELL_SECOND_BY_ORDER[orderIndex] ??
		DEMO_CARD_EXP_PER_DWELL_SECOND_DEFAULT
	);
}

function getDurationScaledDemoGrant(
	cardId: string,
	perDwellSecond: number,
	durationMs = getLandingDemoCardRequiredDurationMs(cardId),
): number {
	const seconds = Math.max(0, durationMs) / 1000;
	return Math.max(0, Math.round(seconds * perDwellSecond));
}

export function getDemoExpGrant(
	topicKey: string,
	cardId: string,
	durationMs = getLandingDemoCardRequiredDurationMs(cardId),
	topicReviseCycle?: number,
): number {
	void topicKey;
	const base = getDurationScaledDemoGrant(
		cardId,
		getDemoCardExpPerDwellSecond(cardId),
		durationMs,
	);
	const cycle =
		typeof topicReviseCycle === "number" &&
		Number.isFinite(topicReviseCycle)
			? Math.max(0, Math.floor(topicReviseCycle))
			: 0;
	return applyTopicCardCompletedRepeatMultiplier(base, cycle);
}

/** Duration-scaled demo card coin reward (local wallet only). */
export function getDemoCoinGrant(
	cardId: string,
	durationMs = getLandingDemoCardRequiredDurationMs(cardId),
): number {
	return getDurationScaledDemoGrant(
		cardId,
		DEMO_CARD_COINS_PER_DWELL_SECOND,
		durationMs,
	);
}

export function getDemoTopicKeyForInteractable(
	featureIndex: number,
	topicIndex: number,
): string {
	const row =
		LANDING_DEMO_INTERACTABLE_KEYS[featureIndex] ??
		LANDING_DEMO_INTERACTABLE_KEYS[0];
	const key = row[topicIndex];
	if (!key) {
		throw new Error(
			`No landing demo topic for featureIndex=${featureIndex} topicIndex=${topicIndex}`,
		);
	}
	return key;
}

export interface LandingDemoInteractableSlot {
	featureIndex: number;
	topicIndex: number;
}

/** Grid slot key for {@link LANDING_DEMO_INTERACTABLE_KEYS} — matches SolutionDemo `interactableKey`. */
export function getLandingDemoInteractableSlotKey(
	featureIndex: number,
	topicIndex: number,
): string {
	return `${featureIndex}-${topicIndex}`;
}

/** Earliest interactable topic not yet marked complete in the demo path. */
export function findLandingDemoResumeInteractableSlot(
	readTopicKeys: ReadonlySet<string>,
	featureIndex?: number,
): LandingDemoInteractableSlot {
	const featureIndexes =
		featureIndex == null
			? Array.from(
					{ length: LANDING_DEMO_INTERACTABLE_KEYS.length },
					(_, index) => index,
				)
			: [featureIndex];

	for (const scopedFeatureIndex of featureIndexes) {
		const row = LANDING_DEMO_INTERACTABLE_KEYS[scopedFeatureIndex];
		if (row == null) {
			continue;
		}
		for (let topicIndex = 0; topicIndex < row.length; topicIndex++) {
			if (
				!readTopicKeys.has(
					getLandingDemoInteractableSlotKey(
						scopedFeatureIndex,
						topicIndex,
					),
				)
			) {
				return { featureIndex: scopedFeatureIndex, topicIndex };
			}
		}
	}

	return {
		featureIndex: featureIndex ?? 0,
		topicIndex: 0,
	};
}

export function hasLandingDemoEstablishedPointer(
	topicProgressByStopId: Readonly<
		Record<string, LandingDemoTopicStopProgress>
	>,
): boolean {
	for (const row of Object.values(topicProgressByStopId)) {
		if (row.completedCardIds.length > 0) {
			return true;
		}
		if (row.resumeCardId != null) {
			return true;
		}
	}
	return false;
}

export function shouldUseLandingDemoResumeLabel(input: {
	readTopicKeys: ReadonlySet<string>;
	topicProgressByStopId: Readonly<
		Record<string, LandingDemoTopicStopProgress>
	>;
	topicViewerOpened: boolean;
}): boolean {
	return (
		input.topicViewerOpened ||
		input.readTopicKeys.size > 0 ||
		hasLandingDemoEstablishedPointer(input.topicProgressByStopId)
	);
}

export function shouldUseLandingDemoResumeLabelForFeature(input: {
	featureIndex: number;
	readTopicKeys: ReadonlySet<string>;
	topicProgressByStopId: Readonly<
		Record<string, LandingDemoTopicStopProgress>
	>;
}): boolean {
	const row = LANDING_DEMO_INTERACTABLE_KEYS[input.featureIndex];
	if (row == null) {
		return false;
	}

	for (let topicIndex = 0; topicIndex < row.length; topicIndex++) {
		if (
			input.readTopicKeys.has(
				getLandingDemoInteractableSlotKey(
					input.featureIndex,
					topicIndex,
				),
			)
		) {
			return true;
		}

		const topicKey = row[topicIndex];
		if (topicKey == null) {
			continue;
		}

		for (const [stopId, progress] of Object.entries(
			input.topicProgressByStopId,
		)) {
			if (!stopId.includes(topicKey)) {
				continue;
			}
			if (
				progress.completedCardIds.length > 0 ||
				progress.resumeCardId != null
			) {
				return true;
			}
		}
	}

	return false;
}

/**
 * Unlock-ring dwell per card — intentionally shorter than autoplay so learners reach Next sooner.
 * Set {@link LANDING_DEMO_CONTINUATION_TIMER_POLICY} to `CALCULATED` to align with autoplay again.
 */
export const LANDING_DEMO_CARD_CONTINUATION_MS = 3000;

export const LANDING_DEMO_CONTINUATION_TIMER_POLICY: TopicContinuationTimerPolicy =
	"FIXED";

/** Autoplay / progress dwell — same block-based estimate as SaaS topic cards (no fixed demo override). */
export function getLandingDemoCardRequiredDurationMs(cardId: string): number {
	for (const topic of Object.values(LANDING_DEMO_TOPICS_BY_KEY)) {
		const card = topic.cards.find((c) => c.id === cardId);
		if (card) {
			return getTopicCardRequiredDurationMs({
				id: card.id,
				blocks: card.blocks,
				isPresentationOnly: card.isPresentationOnly,
				layoutTemplate: card.layoutTemplate,
			});
		}
	}
	return getTopicCardRequiredDurationMs(undefined);
}

const DEMO_COMMENT_AUTHORS = [
	"Jordan Lee",
	"Sam Rivera",
	"Quinn Park",
	"Avery Chen",
	"Morgan Diaz",
	"Riley Brooks",
	"Casey Wu",
	"Taylor Singh",
	"Jordan Kim",
] as const;

const LANDING_DEMO_TOPIC_COMMENT_BODIES: string[][][] = [
	[
		[
			"Talking through the lesson on my phone felt natural – way faster than building slides from scratch.",
			"Dropped a messy deck and some notes; the transcription picked up the details I would have skipped.",
			"We had no source material, but a short brief was enough to get a usable first draft.",
			"Previewing as a learner before publish caught awkward pacing before our team saw it.",
			"The pre-built library modules saved us weeks on onboarding basics.",
		],
		[
			"Scheduling the rollout to our team from one place beat juggling email and the LMS.",
			"Peer review on the draft caught policy wording issues before go-live.",
			"Version history made it easy to roll back an over-eager edit.",
			"Download links for managers who want offline copies were a nice touch.",
			"Approval flow kept compliance in the loop without slowing creators down.",
			"Fresh content updates landed without us rebuilding the whole course.",
		],
		[
			"Finally seeing completion rates next to each module – not just who clicked start.",
			"Sticking-point analytics showed exactly where people dropped off.",
			"Completion time trends helped us trim a module that was running long.",
			"Engagement rates per card surfaced what people actually re-read.",
			"Surfaced insights saved us from digging through spreadsheets.",
			"Experiment suggestions gave us a clear next edit to try.",
			"Data-driven tweaks felt less guesswork than our old survey cycle.",
		],
	],
	[
		[
			"The ogre quest hook made compliance training feel less like punishment.",
			"Leveling up after each card kept me clicking – didn't expect that from work training.",
			"I'm not a gamer, but the pacing still felt approachable.",
			"Good reminder this goes beyond stickers and leaderboards.",
			"Game-based framing helped our team talk about skills, not just checkboxes.",
			"Short battles between cards broke up the reading without feeling childish.",
		],
		[
			"Finishing the module didn't mean I knew how to use it on Monday morning.",
			"Practice prompts after each concept actually tested understanding.",
			"Role-play with the coach was the first time feedback felt situational.",
			"Applying scenarios to our team context was the missing step in other tools.",
			"Habit check-ins made the follow-through feel accountable, not optional.",
			"STEP framing from learn to apply to habits is how our L&D team talks now.",
			"Less slide fatigue, more reps – that's what we needed.",
		],
		[
			"The in-world coach felt like a guide, not a chatbot bolted on.",
			"Asked for a deeper explanation mid-card and got examples that matched my role.",
			"Switching between read and listen modes matched how I study on commutes.",
			"Personalized pacing kept the quiz from feeling like a generic survey.",
			"Goal tracking tied the module back to my development plan.",
			"Agents nudged me when I stalled without spamming notifications.",
			"Practice quizzes on demand beat waiting for a scheduled workshop.",
			"Felt like a coach on call, not a static course.",
		],
	],
	[
		[
			"Having agents available 24/7 beat waiting for someone in another timezone.",
			"Jumping from chat into the right topic saved a lot of menu hunting.",
			"Asked where a policy lived and got pointed to the exact module.",
			"Search across our org knowledge felt faster than our shared drive.",
			"Useful for leaders who need answers without opening five tabs.",
			"Context-aware replies referenced our goals, not generic help articles.",
			"Nice that conversations follow you across the platform.",
		],
		[
			"Automating the report pull saved our ops lead an hour every Monday.",
			"Dashboard spun up from a question instead of a ticket to BI.",
			"Behavioral metrics beyond completions is what our exec team asked for.",
			"Less clicking through admin screens to get a simple cohort view.",
			"On-the-fly analytics made our retro meeting actually data-backed.",
			"Workflow shortcuts in chat beat learning another admin console.",
			"Seeing application signals, not just course finished flags.",
			"Leaders finally got a view into habits, not only completions.",
		],
		[
			"Submitted a feature request from the demo – hope the roadmap tag sticks.",
			"Roadmap transparency makes early access feel collaborative.",
			"Emailed the team with a specific question and got a helpful reply.",
			"Waitlist signup was quick; excited to try this with a pilot group.",
			"Feature voting gives us a voice before launch.",
			"Good to see customer feedback called out explicitly.",
			"Early access path feels clear after this walkthrough.",
			"Appreciate the offer to talk through fit before committing.",
			"Made it through all nine topics – signing up for the waitlist next.",
		],
	],
];

function demoCommentsForCell(
	f: number,
	t: number,
): LandingDemoTopicCommentSeed[] {
	const bodies = LANDING_DEMO_TOPIC_COMMENT_BODIES[f]?.[t] ?? [];
	const base = `landing-demo-f${f}-t${t}`;
	return bodies.map((body, index) => ({
		id: `${base}-c${index + 1}`,
		userDisplayName:
			DEMO_COMMENT_AUTHORS[index % DEMO_COMMENT_AUTHORS.length] ??
			"Guest",
		body,
		createdAtIso: `2026-04-${String(20 + f * 3 + t).padStart(2, "0")}T${String(9 + index).padStart(2, "0")}:${String((index * 11) % 60).padStart(2, "0")}:00.000Z`,
	}));
}

function engagementCountsForCell(
	f: number,
	t: number,
): LandingDemoTopicSocialSeed {
	const comments = demoCommentsForCell(f, t);
	const baseLike = 24 + f * 9 + t * 3;
	const baseSave = 8 + f * 2 + t;
	const baseShare = 3 + ((f + t) % 4);
	return {
		counts: {
			likeCount: baseLike,
			saveCount: baseSave,
			commentCount: comments.length,
			shareCount: baseShare,
		},
		viewer: {
			hasLiked: false,
			hasSaved: false,
			hasCommented: false,
			hasShared: false,
		},
		comments,
	};
}

/** Topic engagement + demo comments keyed by topic id (`landing-demo-f*` ids). */
export const LANDING_DEMO_SOCIAL_BY_TOPIC_ID: Record<
	string,
	LandingDemoTopicSocialSeed
> = (() => {
	const map: Record<string, LandingDemoTopicSocialSeed> = {};
	for (let f = 0; f < 3; f++) {
		for (let t = 0; t < 3; t++) {
			const key = demoTopicKey(f, t);
			map[key] = engagementCountsForCell(f, t);
		}
	}
	return map;
})();

export function getLandingDemoSocialForTopicId(
	topicId: string | undefined,
): LandingDemoTopicSocialSeed | undefined {
	if (!topicId) {
		return undefined;
	}
	return LANDING_DEMO_SOCIAL_BY_TOPIC_ID[topicId];
}

export function mergeLandingDemoTopicSocial(
	base: LandingDemoTopicSocialSeed,
	overlay: LandingDemoTopicEngagementPersisted | undefined,
): LandingDemoTopicSocialSeed {
	if (!overlay) {
		return base;
	}
	return {
		...base,
		counts: {
			...base.counts,
			likeCount: overlay.likeCount,
			saveCount: overlay.saveCount,
		},
		viewer: {
			...base.viewer,
			hasLiked: overlay.hasLiked,
			hasSaved: overlay.hasSaved,
		},
	};
}
